"use client"

import { forwardRef, type ComponentProps } from "react"
import { Popover as PopoverPrimitive } from "@base-ui/react/popover"
import { Popover, PopoverContent, PopoverTrigger } from "./popover"
import { cn } from "../lib/utils"

/**
 * Popover-based combobox primitives — the shadcn-style Popover + Command
 * pattern, wrapped so the trigger pill, popover container, and default
 * positioning are native to the component instead of being copy-pasted as
 * classNames at every call site.
 *
 * Distinct from `combobox.tsx` in this package, which wraps the base-ui
 * `<Combobox>` primitive (combobox-with-text-input pattern).
 *
 * Consumers provide `<Command>` children inside `<ComboboxContent>`. The
 * trigger picks up the `.combobox-trigger` class styled by the consuming
 * app's theme (connect: new-ui-theme.css) so hover/focus/padding stay
 * consistent across every combobox.
 *
 *   <Combobox open={open} onOpenChange={setOpen}>
 *     <ComboboxTrigger muted>
 *       Pick something <ChevronsUpDown />
 *     </ComboboxTrigger>
 *     <ComboboxContent>
 *       <Command>...</Command>
 *     </ComboboxContent>
 *   </Combobox>
 */

export const Combobox = Popover

type TriggerProps = ComponentProps<"button"> & {
  /** Placeholder-style muted text when no value is selected. */
  muted?: boolean
}

export const ComboboxTrigger = forwardRef<HTMLButtonElement, TriggerProps>(
  function ComboboxTrigger({ className, muted, children, ...props }, ref) {
    return (
      <PopoverTrigger
        render={
          <button
            ref={ref}
            type="button"
            data-slot="combobox-pill-trigger"
            className={cn(
              "combobox-trigger",
              muted &&
                "text-[var(--muted-foreground)] hover:text-[var(--foreground)]",
              className
            )}
            {...props}
          />
        }
      >
        {children}
      </PopoverTrigger>
    )
  }
)

type ContentProps = ComponentProps<typeof PopoverContent>

export const ComboboxContent = forwardRef<HTMLDivElement, ContentProps>(
  function ComboboxContent(
    { className, align = "start", sideOffset = 6, children, ...props },
    ref
  ) {
    return (
      <PopoverContent
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        data-slot="combobox-pill-content"
        positionerClassName="max-md:!z-[80] max-md:!isolate max-md:!transform-none max-md:!top-0 max-md:!left-0 max-md:!right-0 max-md:!bottom-0 max-md:!w-screen max-md:!h-[100dvh]"
        className={cn(
          "new-ui-theme w-[280px] p-0 bg-[var(--popover)] text-[var(--popover-foreground)] border border-[var(--border)] shadow-lg",
          "max-md:fixed max-md:inset-0 max-md:z-[80] max-md:w-screen max-md:max-w-none max-md:h-[100dvh] max-md:rounded-none max-md:border-0 max-md:shadow-none max-md:flex max-md:flex-col",
          "max-md:pt-[calc(env(safe-area-inset-top)+0.75rem)] max-md:pb-[env(safe-area-inset-bottom)]",
          className
        )}
        {...props}
      >
        {/* Mobile: a clean close affordance on its own right-aligned row above
            the search — no absolute overlap of the input to mis-align. */}
        <PopoverPrimitive.Close
          aria-label="Close"
          className="mr-2 mb-1 ml-auto hidden max-md:inline-flex items-center justify-center rounded-md border border-black/15 bg-transparent px-2.5 py-1 text-xs font-medium text-[var(--muted-foreground)] dark:border-white/15"
        >
          Esc
        </PopoverPrimitive.Close>
        {children}
      </PopoverContent>
    )
  }
)
